import React, { useState } from "react";
import { useDispatch } from "react-redux";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import PropTypes from "prop-types";
import {
  getRenewalPostDoc,
  adminConfirm
} from "../../../../core/service/renewalPostDoc.service";
import moment from "moment";
import excelJs from "exceljs";
import Page from "../../../Page";
import UserListToolbar from "./UserListToolbar";
import UserListHead from "./UserListHead";
import SearchNotFound from "../../../SearchNotFound";
import MoreVertIcon from "@mui/icons-material/MoreVert";

import {
  Card,
  Container,
  Table,
  Stack,
  TableRow,
  TableBody,
  TableCell,
  Typography,
  TableContainer,
  TablePagination,
  IconButton,
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  List,
  ListItem,
  Link
} from "@mui/material";
import { config } from "../../../../core/service/config.service";

const TABLE_HEAD = [
  { id: "name", label: "Name", alignRight: false },
  { id: "email", label: "Email", alignRight: false },
  { id: "mentor", label: "Mentor", alignRight: false },
  { id: "contractDate", label: "Contract Date", alignRight: false }
];

const ROLE_ADMIN = "admin";
const ROLE_MENTOR = "mentor";
const ROLE_POSTDOC_3Y = "post_doc_3y";
const ROLE_POSTDOC_5Y = "post_doc_5y";
const ROLE_PHD = "PhD";

const descendingComparator = (a, b, orderBy) => {
  if (b[orderBy] < a[orderBy]) {
    return -1;
  }
  if (b[orderBy] > a[orderBy]) {
    return 1;
  }
  return 0;
};

const getComparator = (order, orderBy) => {
  return order === "desc"
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
};

const applySortFilter = (array, comparator, query) => {
  const stabilizedThis = array?.map((el, index) => [el, index]);
  stabilizedThis?.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) return order;
    return a[1] - b[1];
  });
  if (query) {
    return array.filter((e) => {
      const { title_en, name_en, middle_en, surname_en } = e;
      const name = `${title_en} ${name_en} ${middle_en || ""} ${surname_en}`;
      return name.toLowerCase().indexOf(query.toLowerCase()) !== -1;
    });
  }
  return stabilizedThis.map((el) => el[0]);
};

RenewalFellowshipAdminTable.propTypes = {
  renewalList: PropTypes.array
};

export default function RenewalFellowshipAdminTable({ renewalList }) {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [page, setPage] = useState(0);
  const [order, setOrder] = useState("asc");
  const [orderBy, setOrderBy] = useState("name");
  const [filterName, setFilterName] = useState("");
  const [rowsPerPage, setRowsPerPage] = useState(5);

  const [openDetailRenewal, setOpenDetailRenewal] = useState(false);
  const [moreDetailListRenewal, setMoreDetailListRenewal] = useState({});

  const openDetail = async (row) => {
    const { id } = row;
    const moreDetail = await getRenewalPostDoc(id);
    setMoreDetailListRenewal(moreDetail);
    setOpenDetailRenewal(true);
  };

  const closeMoreDetail = () => {
    setMoreDetailListRenewal({});
    setOpenDetailRenewal(false);
  };

  const confirm = async (row) => {
    const { id } = row;
    await adminConfirm(id);
    closeMoreDetail();
  };

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleFilterByName = (event) => {
    setFilterName(event.target.value);
  };

  const emptyRows =
    page > 0 ? Math.max(0, (1 + page) * rowsPerPage - renewalList.length) : 0;

  const filteredUsers = applySortFilter(
    renewalList,
    getComparator(order, orderBy),
    filterName
  );

  const isUserNotFound = filteredUsers.length === 0;

  const exportExcel = async () => {
    const workbook = new excelJs.Workbook();
    const ws = workbook.addWorksheet(`Report`);
    ws.columns = [
      {
        header: "Name",
        key: "name_en",
        width: 10,
        style: {
          alignment: { horizontal: "center", vertical: "middle" },
          font: { bold: true }
        }
      },
      {
        header: "Email",
        key: "email",
        width: 10,
        style: {
          alignment: { horizontal: "center", vertical: "middle" },
          font: { bold: true }
        }
      },
      {
        header: "Mentor",
        key: "mentor",
        width: 10,
        style: {
          alignment: { horizontal: "center", vertical: "middle" },
          font: { bold: true }
        }
      },
      {
        header: "Contract Date",
        key: "contractDate",
        width: 10,
        style: {
          alignment: { horizontal: "center", vertical: "middle" },
          font: { bold: true }
        }
      }
    ];

    ws.getRow(1).height = 30;

    await Promise.all(
      filteredUsers.map(async (val, index) => {
        ws.addRow([
          `${val.name_en} ${val.middle_en || ""} ${val.surname_en}`,
          val.email,
          `${val.mentor_title_en} ${val.mentor_name_en} ${val.mentor_middle_en}`,
          val.created_date
        ]);
        ws.getRow(index + 2).font = {
          ...ws.getRow(index + 2).font,
          bold: false
        };
      })
    );

    workbook.xlsx
      .writeBuffer({
        base64: true
      })
      .then((xls64) => {
        // build anchor tag and attach file (works in chrome)
        var a = document.createElement("a");
        var data = new Blob([xls64], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
        });

        var url = URL.createObjectURL(data);
        a.href = url;
        a.download = `RENEWAL_OF_FELLOWSHIP_${moment().format(
          "YYYYMMDD_HHmmss"
        )}.xlsx`;
        document.body.appendChild(a);
        a.click();
        setTimeout(function () {
          document.body.removeChild(a);
          window.URL.revokeObjectURL(url);
        }, 0);
      })
      .catch(function (error) {
        console.log(error.message);
      });
  };

  return (
    <Page title="Renewal of Fellowship Admin">
      <Container>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          mb={5}
        >
          <Typography variant="h4" gutterBottom>
            Renewal of Fellowship
          </Typography>
       
          <Link href={`${config.host_api}export/renewal-fellowship`}>
            <Button variant="contained">Export excel</Button>
          </Link>
        </Stack>

        <Card>
          <UserListToolbar
            filterName={filterName}
            onFilterName={handleFilterByName}
          />

          <TableContainer sx={{ minWidth: 800 }}>
            <Table>
              <UserListHead
                order={order}
                orderBy={orderBy}
                headLabel={TABLE_HEAD}
                rowCount={renewalList.length}
                onRequestSort={handleRequestSort}
              />
              <TableBody>
                {filteredUsers
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((row, i) => {
                    const {
                      name_en,
                      middle_en,
                      email,
                      mentor_middle_en,
                      mentor_name_en,
                      surname_en,
                      mentor_title_en,
                      created_date
                    } = row;

                    return (
                      <TableRow tabIndex={-1} hover key={i}>
                        <TableCell />

                        <TableCell component="th" scope="row" padding="none">
                          <Stack
                            direction="row"
                            alignItems="center"
                            spacing={2}
                          >
                            <Typography>
                              {`${name_en} ${middle_en} ${surname_en}` || ""}
                            </Typography>
                          </Stack>
                        </TableCell>

                        <TableCell align="left">{email}</TableCell>

                        <TableCell align="left">
                          {`${mentor_title_en} ${mentor_name_en} ${mentor_middle_en}` ||
                            ""}
                        </TableCell>

                        <TableCell align="left">{created_date}</TableCell>

                        <TableCell align="left">
                          <IconButton onClick={() => openDetail(row)}>
                            <MoreVertIcon />
                          </IconButton>
                        </TableCell>

                        <Dialog
                          open={openDetailRenewal}
                          onClose={closeMoreDetail}
                          fullWidth={true}
                          maxWidth={"lg"}
                        >
                          <DialogContent>
                            <Stack alignItems="center">
                              <Typography variant="h4">
                                Renewal of Fellowship
                              </Typography>
                            </Stack>
                            <Grid container spacing={2}>
                              <Grid item xs>
                                <List>
                                  <ListItem>
                                    <Stack direction="row" spacing={3}>
                                      <Typography variant="subtitle1">
                                        Name:
                                      </Typography>
                                      <Typography>{`${moreDetailListRenewal?.name_en} ${moreDetailListRenewal?.middle_en} ${moreDetailListRenewal?.surname_en}`}</Typography>
                                    </Stack>
                                  </ListItem>

                                  <ListItem>
                                    <Stack direction="row" spacing={3}>
                                      <Typography variant="subtitle1">
                                        Email:
                                      </Typography>
                                      <Typography>
                                        {moreDetailListRenewal?.email}
                                      </Typography>
                                    </Stack>
                                  </ListItem>

                                  <ListItem>
                                    <Stack direction="row" spacing={3}>
                                      <Typography variant="subtitle1">
                                        Year:
                                      </Typography>
                                      <Typography>
                                        {moreDetailListRenewal?.year_contract}
                                      </Typography>
                                    </Stack>
                                  </ListItem>

                                  <ListItem>
                                    <Stack
                                      direction="row"
                                      spacing={3}
                                      sx={{ paddingTop: 2 }}
                                    >
                                      <Typography variant="subtitle1">
                                        Request Date:
                                      </Typography>
                                      <Typography>
                                        {moreDetailListRenewal?.created_date}
                                      </Typography>
                                    </Stack>
                                  </ListItem>

                                  <ListItem>
                                    <Stack direction="row" spacing={3}>
                                      <Typography variant="subtitle1">
                                        Note:
                                      </Typography>
                                      <Typography>
                                        {
                                          moreDetailListRenewal?.additional_inform_note
                                        }
                                      </Typography>
                                    </Stack>
                                  </ListItem>

                                  <ListItem>
                                    <Stack
                                      direction="row"
                                      spacing={3}
                                      sx={{ paddingTop: 2 }}
                                    >
                                      <Typography variant="subtitle1">
                                        Mentor Name:
                                      </Typography>
                                      <Typography>{`${moreDetailListRenewal?.mentor_title_en} 
                                  ${moreDetailListRenewal?.mentor_name_en} 
                                  ${moreDetailListRenewal?.mentor_middle_en} 
                                  ${moreDetailListRenewal?.mentor_surname_en}`}</Typography>
                                    </Stack>
                                  </ListItem>

                                  <ListItem>
                                    <Stack direction="row" spacing={3}>
                                      <Typography variant="subtitle1">
                                        Contract Date:
                                      </Typography>
                                      <Typography>
                                        {moreDetailListRenewal?.mentor_start_new_contract ||
                                          "-"}
                                      </Typography>
                                    </Stack>
                                  </ListItem>

                                  <ListItem>
                                    <Stack direction="row" spacing={3}>
                                      <Typography variant="subtitle1">
                                        Mentor Note:
                                      </Typography>
                                      <Typography>
                                        {moreDetailListRenewal?.mentor_note ||
                                          "-"}
                                      </Typography>
                                    </Stack>
                                  </ListItem>
                                </List>
                              </Grid>
                            </Grid>

                            {moreDetailListRenewal?.admin_approve === 0 && (
                              <DialogActions>
                                <Button
                                  onClick={() => confirm(moreDetailListRenewal)}
                                  autoFocus
                                >
                                  Confirm
                                </Button>
                              </DialogActions>
                            )}
                          </DialogContent>
                        </Dialog>
                      </TableRow>
                    );
                  })}
                {emptyRows > 0 && (
                  <TableRow style={{ height: 53 * emptyRows }}>
                    <TableCell colSpan={6} />
                  </TableRow>
                )}
              </TableBody>
              {isUserNotFound && (
                <TableBody>
                  <TableRow>
                    <TableCell align="center" colSpan={6} sx={{ py: 3 }}>
                      <SearchNotFound searchQuery={filterName} />
                    </TableCell>
                  </TableRow>
                </TableBody>
              )}
            </Table>
          </TableContainer>

          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={renewalList.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </Card>
      </Container>
    </Page>
  );
}
